import { convertPsychological, type SupportedCurrency } from "./currency";

/**
 * Per-line money for a /checkout cart, the client half of the server's
 * oneTimeCatalogAmounts + perLineCharge
 * (services/api/internal/handler/checkout_amount_override.go).
 *
 * A product line is priced by THAT product: its own active per-currency
 * catalog amount when it has one, else its own psychological FX. It is never
 * looked up by USD amount, because two cart products can share a USD price
 * while only one of them is catalogued (the amount-keyed lookup showed the
 * uncatalogued one at the other's curated price, while the server charges it
 * at FX). Add-ons always convert one by one, never as a sum: psych rounding is
 * not additive.
 */
export interface CartLineProduct {
  price_cents: number;
  per_currency_prices?: Record<string, number>;
}

export function productLineCents(
  p: CartLineProduct,
  currency: SupportedCurrency,
  rates: Record<string, number>,
): number {
  if (currency === "USD") return p.price_cents;
  const catalog = p.per_currency_prices?.[currency];
  if (typeof catalog === "number" && catalog > 0) return catalog;
  return convertPsychological(p.price_cents, currency, rates);
}

/**
 * The amount the page shows and sends as `amount_override_cents`: every
 * product line (quantity 1, as /checkout sends it) plus each add-on on its
 * own. USD is the plain USD sum.
 */
export function cartChargeCents(
  products: CartLineProduct[],
  addonsUsd: number[],
  currency: SupportedCurrency,
  rates: Record<string, number>,
): number {
  const lines = products.reduce((s, p) => s + productLineCents(p, currency, rates), 0);
  const addons = addonsUsd.reduce((s, a) => s + (a > 0 ? convertPsychological(a, currency, rates) : 0), 0);
  return lines + addons;
}

/**
 * The rate sent as `fx_rate` next to `amount_override_cents`: the USD-base
 * rate (1 USD = rate) this page priced the cart with. The server charges the
 * client figure only when it equals its own per-line recompute at THIS rate
 * (settleNonUSDOneTime), so a buyer whose cached rate sits across a 7/9 snap
 * boundary from the server's (A$9 vs A$19 for $9) pays what the page showed.
 * 0 for USD or a missing rate: the server then charges its own figure.
 */
export function chargeFxRate(currency: SupportedCurrency, rates: Record<string, number>): number {
  if (currency === "USD") return 0;
  const r = rates[currency];
  return typeof r === "number" && Number.isFinite(r) && r > 0 ? r : 0;
}
